import { useEffect, useRef, useState } from "react"

import { Anchor } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"

import styles from "./Aside.module.scss"

import type { AsideComponent } from "."

const CONTAINER_ID = "PageWithBreadcrumbTemplate"
// Where a section lands under the sticky header when its link is clicked
const TARGET_OFFSET = 80
// How long a clicked link stays active while the page scrolls to its section
const CLICK_SCROLL_MS = 600

const getContainer = () => document.getElementById(CONTAINER_ID)

// The section crossing the top of the scroll panel; the last one once the
// panel is scrolled to its end, as a short last section never reaches the top
export const getActiveSection = (keys: string[], container: HTMLElement) => {
  const { scrollTop, clientHeight, scrollHeight } = container
  const sections = keys.filter((key) => document.getElementById(key))

  if (scrollTop > 0 && scrollTop + clientHeight >= scrollHeight - 1)
    return sections[sections.length - 1]

  const top = container.getBoundingClientRect().top + TARGET_OFFSET

  return sections.reduce<string | undefined>(
    (active, key) =>
      document.getElementById(key)!.getBoundingClientRect().top <= top + 1
        ? key
        : active,
    sections[0]
  )
}

const useActiveSection = (keys: string[], enabled: boolean) => {
  const [active, setActive] = useState<string>()
  const clickedUntil = useRef(0)

  useEffect(() => {
    const container = getContainer()

    if (!enabled || !container) return

    const update = () => {
      if (Date.now() < clickedUntil.current) return

      setActive(getActiveSection(keys, container))
    }

    update()
    container.addEventListener("scroll", update, { passive: true })

    return () => container.removeEventListener("scroll", update)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, keys.join()])

  return {
    active,
    // A clicked section stays active even when the page cannot scroll it to
    // the top
    onClick: (key: string) => {
      clickedUntil.current = Date.now() + CLICK_SCROLL_MS
      setActive(key)
    }
  }
}

export const Aside: AsideComponent = (props) => {
  const { items, followScroll = false } = props
  const { t } = useTranslation()
  const getItems = () =>
    items.map((item) => {
      const { key, tKey = "", title = t(tKey) } = item

      return { key, title, href: `#${key}` }
    })
  const anchorItems = useRef(getItems()).current
  const ref = useRef<HTMLDivElement>(null)
  const section = useActiveSection(
    anchorItems.map(({ key }) => key),
    followScroll
  )
  const activeHref = followScroll && section.active && `#${section.active}`

  useEffect(() => {
    if (!followScroll) return

    ref.current
      ?.querySelectorAll<HTMLAnchorElement>("a[href^='#']")
      .forEach((link) => {
        if (link.getAttribute("href") === activeHref)
          link.setAttribute("aria-current", "location")
        else link.removeAttribute("aria-current")
      })
  }, [followScroll, activeHref])

  const anchor = (
    <Anchor
      replace
      className={styles.container}
      items={anchorItems}
      getCurrentAnchor={(activeLink) =>
        activeHref || activeLink || anchorItems[0]?.href
      }
      onClick={(_, link) =>
        followScroll && section.onClick(link.href.replace(/^#/, ""))
      }
      targetOffset={TARGET_OFFSET}
      getContainer={() => getContainer() || window}
    />
  )

  if (!followScroll) return anchor

  return <div ref={ref}>{anchor}</div>
}
